/**
 * versona_names (client MIRROR of server/src/lib/versona_names.ts — keep the
 * syllable tables and h01 salts byte-identical, or the same versona answers
 * to different names per surface). V1: every soul gets a NAME.
 *
 * The civ is the one a soul was BORN into (`versona_origin`, 2026-09-15 —
 * subscribed always-on by EnvironmentModule); the current settlement's
 * `settlement_civ` row is only the fallback for a soul with no origin row
 * yet. Same order as the server's nameOf, so a migrant or a conquered town's
 * resident keeps their name on every surface.
 */

function fnv(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  return h >>> 0;
}
// Avalanche finalizer (mirror of the server's nameMix) — raw FNV-1a leaves
// the 'fn1'/'fn2' salts correlated in the high bits, collapsing name variety.
function nameMix(h: number): number {
  h ^= h >>> 16; h = Math.imul(h, 0x85ebca6b); h ^= h >>> 13; h = Math.imul(h, 0xc2b2ae35); h ^= h >>> 16;
  return h >>> 0;
}
const nameRoll = (id: string, salt: string): number => nameMix(fnv(`${id}|${salt}`)) / 0xffffffff;

const AURELIAN_A = ['Al', 'Aure', 'Cas', 'Del', 'Fal', 'Iva', 'Lor', 'Mar', 'Nel', 'Or', 'Pel', 'Ques', 'Ren', 'Sera', 'Tav', 'Val', 'Vin', 'Zan', 'Cal', 'Mira'];
const AURELIAN_B = ['ius', 'ia', 'en', 'on', 'ara', 'elle', 'io', 'ita', 'or', 'ine', 'us', 'a', 'ette', 'ino', 'is', 'ea'];
const BOREAN_A = ['Bjor', 'Hald', 'Inga', 'Kel', 'Ragn', 'Sig', 'Thor', 'Ulf', 'Vald', 'Yrsa', 'Ask', 'Eir', 'Gud', 'Hren', 'Kar', 'Odd', 'Skar', 'Torv', 'Brend', 'Frey'];
const BOREAN_B = ['ar', 'ild', 'grim', 'ny', 'olf', 'rid', 'stein', 'a', 'ir', 'mund', 'veig', 'dis', 'orn', 'ulf', 'res', 'ka'];

/** Deterministic civ-flavored first name ('a' Aurelian, 'b' Borean). */
export function firstNameOf(versonaId: bigint, civ: string): string {
  const A = civ === 'b' ? BOREAN_A : AURELIAN_A;
  const B = civ === 'b' ? BOREAN_B : AURELIAN_B;
  const a = A[Math.floor(nameRoll(String(versonaId), 'fn1') * A.length) % A.length];
  const b = B[Math.floor(nameRoll(String(versonaId), 'fn2') * B.length) % B.length];
  return a + b;
}

interface CivRowLite { settlementId: bigint; civ: string }
interface OriginLite { versonaId: bigint; civ: string }
interface AgentLite { versonaId: bigint; settlementId: bigint }
interface MemberLite { householdId: bigint; versonaId: bigint }
interface HouseholdLite { householdId: bigint; surname: string }

/** Full name from replicated tables ("Bjorild Galridge"); first name alone
 *  when the household tables aren't subscribed yet. */
export function nameFromTables(conn: unknown, versonaId: bigint): string {
  const origin = rowBy<OriginLite>(conn, 'versonaOrigin', 'versonaId', 'versonaId', versonaId);
  const agent = origin ? undefined : rowBy<AgentLite>(conn, 'npcAgent', 'versonaId', 'versonaId', versonaId);
  const civ = origin?.civ ?? (agent ? (rowBy<CivRowLite>(conn, 'settlementCiv', 'settlementId', 'settlementId', agent.settlementId)?.civ ?? 'a') : 'a');
  const first = firstNameOf(versonaId, civ);
  const member = rowBy<MemberLite>(conn, 'householdMember', 'householdMemberByVersona', 'versonaId', versonaId);
  const surname = member ? rowBy<HouseholdLite>(conn, 'household', 'householdId', 'householdId', member.householdId)?.surname : undefined;
  return surname ? `${first} ${surname}` : first;
}

/**
 * ONE row by key, through the table's index accessor. WHY (2026-09-20): this used to SPREAD each whole table into an
 * array and `.find` it — versona_origin is 14k rows, and a name costs five such lookups. The combat reticle names
 * every versona within 80 m ten times a second, so a festival crowd of forty cost ~46 ms per scan on the main thread
 * ("12 fps at a city event"). The accessors are real indexes now (lib/fast_find.ts); a table without one — a harness
 * fake, a schema older than the accessor — is walked once, never copied.
 */
function rowBy<T>(conn: unknown, table: string, accessor: string, column: string, key: unknown): T | undefined {
  try {
    const t = (conn as { db: Record<string, (Record<string, unknown> & { iter?(): Iterable<Record<string, unknown>> }) | undefined> }).db[table];
    if (!t) return undefined;
    const idx = t[accessor] as { find?(k: unknown): unknown; filter?(k: unknown): Iterable<unknown> } | undefined;
    if (idx && typeof idx.find === 'function') return (idx.find(key) ?? undefined) as T | undefined;
    if (idx && typeof idx.filter === 'function') { for (const r of idx.filter(key)) return r as T; return undefined; }
    if (typeof t.iter === 'function') for (const r of t.iter()) if (r[column] === key) return r as unknown as T;
  } catch { /* not subscribed yet */ }
  return undefined;
}
